import type {
  CreateMerchantCostRuleRequest,
  MerchantCostRuleDeleteResponse,
  MerchantCostRuleResponse,
  UpdateMerchantCostRuleRequest,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Merchant cost rules: what a merchant's own goods and partners' shares cost
 * it, as a percentage, a flat amount or both, merchant-wide or for one shop,
 * over a validity window.
 *
 * A rule is a record, not an instruction. It writes no billing ledger entry and
 * moves no money. Its rate, category and scope cannot be edited; a price change
 * is a new rule with a new window.
 *
 * Every route needs a dashboard session for the merchant: listing needs the
 * merchant account read permission, every change the write permission. A
 * shop-scoped session reaches nothing here.
 */
export class CostRules {
  constructor(private readonly request: RequestFn) {}

  /**
   * Every cost rule of the merchant.
   * `GET /cost-rules`
   *
   * @param merchantId - The merchant account ID.
   */
  async list(merchantId: string): Promise<MerchantCostRuleResponse[]> {
    return this.request('GET', '/cost-rules', { query: { merchant_id: merchantId } });
  }

  /**
   * Record a cost rule.
   * `POST /cost-rules`
   *
   * @param params - The rule.
   * @param merchantId - The merchant account ID.
   */
  async create(
    params: CreateMerchantCostRuleRequest,
    merchantId: string,
  ): Promise<MerchantCostRuleResponse> {
    return this.request('POST', '/cost-rules', {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Close, deactivate or rename a cost rule.
   * `PUT /cost-rules/{rule_id}`
   *
   * @param ruleId - The rule ID.
   * @param params - What to change.
   * @param merchantId - The merchant account ID.
   */
  async update(
    ruleId: string,
    params: UpdateMerchantCostRuleRequest,
    merchantId: string,
  ): Promise<MerchantCostRuleResponse> {
    return this.request('PUT', `/cost-rules/${encodeURIComponent(ruleId)}`, {
      body: params,
      query: { merchant_id: merchantId },
    });
  }

  /**
   * Delete a cost rule.
   * `DELETE /cost-rules/{rule_id}`
   *
   * @param ruleId - The rule ID.
   * @param merchantId - The merchant account ID.
   */
  async delete(ruleId: string, merchantId: string): Promise<MerchantCostRuleDeleteResponse> {
    return this.request('DELETE', `/cost-rules/${encodeURIComponent(ruleId)}`, {
      query: { merchant_id: merchantId },
    });
  }
}
